import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Loader } from "rsuite";
import CloseIcon from "@rsuite/icons/Close";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import PageIcon from "@rsuite/icons/Page";
import styles from "./FilePreviewModal.module.css";
import { useFilePreviewModal } from "./useFilePreviewModal";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const getExtension = (name = "") => name.split(".").pop()?.toLowerCase() || "";

function FilePreviewModal() {
	const { display, file, closeFilePreview } = useFilePreviewModal();

	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const dragOffset = useRef({ x: 0, y: 0 });

	// Blob object URL used for inline rendering. Fetching the file into a blob
	// strips any `Content-Disposition: attachment` header the storage returns,
	// so the <img>/<iframe> renders inline instead of triggering a download.
	const [blobUrl, setBlobUrl] = useState(null);
	const [blobError, setBlobError] = useState(false);

	// Reset position each time the modal opens.
	useEffect(() => {
		if (display) setPosition({ x: 0, y: 0 });
	}, [display]);

	// Fetch the file as a blob whenever the previewed file changes. Revoke the
	// previous object URL to avoid leaks.
	useEffect(() => {
		let revoked = false;
		let createdUrl = null;
		setBlobUrl(null);
		setBlobError(false);

		const src = file?.url;
		if (!display || !src) return;

		fetch(src, { credentials: "include" })
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.blob();
			})
			.then((blob) => {
				if (revoked) return;
				createdUrl = URL.createObjectURL(blob);
				setBlobUrl(createdUrl);
			})
			.catch(() => {
				if (!revoked) setBlobError(true);
			});

		return () => {
			revoked = true;
			if (createdUrl) URL.revokeObjectURL(createdUrl);
		};
	}, [display, file?.url]);

	// Lock body scroll while open.
	useEffect(() => {
		document.body.style.overflow = display ? "hidden" : "unset";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [display]);

	const handleMouseDown = useCallback(
		(e) => {
			e.preventDefault();
			setIsDragging(true);
			dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
		},
		[position]
	);

	const handleMouseMove = useCallback(
		(e) => {
			if (!isDragging) return;
			setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
		},
		[isDragging]
	);

	const handleMouseUp = useCallback(() => setIsDragging(false), []);

	useEffect(() => {
		if (isDragging) {
			window.addEventListener("mousemove", handleMouseMove);
			window.addEventListener("mouseup", handleMouseUp);
		}
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	if (!display || !file) return null;

	const { name, url } = file;
	const ext = getExtension(name);
	const isImage = IMAGE_EXTS.includes(ext);
	const isPdf = ext === "pdf";

	const displayName = name?.length > 50 ? name.slice(0, 50) + "..." : name;

	return (
		<div className={styles.overlay}>
			<div className={styles.backdrop} onClick={closeFilePreview} />

			<div
				className={styles.modal}
				style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
			>
				{/* Header */}
				<div className={styles.header}>
					<div
						className={styles.drag_handle}
						onMouseDown={handleMouseDown}
						title="Drag to move"
					>
						<span />
						<span />
						<span />
					</div>

					<h2 className={styles.title} title={name}>
						{displayName}
					</h2>

					<div className={styles.header_actions}>
						<a href={url} target="_blank" rel="noopener noreferrer" download>
							<Button size="sm" appearance="ghost" startIcon={<FileDownloadIcon />}>
								Download
							</Button>
						</a>
						<button className={styles.close_btn} onClick={closeFilePreview} aria-label="Close">
							<CloseIcon />
						</button>
					</div>
				</div>

				{/* Content */}
				<div className={styles.content}>
					{(isImage || isPdf) && blobError && (
						<div className={styles.fallback}>
							<PageIcon style={{ fontSize: 40, color: "#94a3b8" }} />
							<p className={styles.fallback_name}>{name}</p>
							<p className={styles.fallback_hint}>Unable to load preview for this file</p>
							<a href={url} target="_blank" rel="noopener noreferrer" download>
								<Button appearance="primary" color="blue" startIcon={<FileDownloadIcon />}>
									Download File
								</Button>
							</a>
						</div>
					)}

					{(isImage || isPdf) && !blobError && !blobUrl && (
						<div className={styles.fallback}>
							<Loader size="md" />
							<p className={styles.fallback_hint}>Loading preview...</p>
						</div>
					)}

					{isImage && blobUrl && (
						<div className={styles.image_wrap}>
							<img src={blobUrl} alt={name} className={styles.image} />
						</div>
					)}

					{isPdf && blobUrl && (
						<iframe src={blobUrl} title={name} className={styles.pdf} />
					)}

					{!isImage && !isPdf && (
						<div className={styles.fallback}>
							<PageIcon style={{ fontSize: 40, color: "#94a3b8" }} />
							<p className={styles.fallback_name}>{name}</p>
							<p className={styles.fallback_hint}>Preview not available for this file type</p>
							<a href={url} target="_blank" rel="noopener noreferrer" download>
								<Button appearance="primary" color="blue" startIcon={<FileDownloadIcon />}>
									Download File
								</Button>
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default FilePreviewModal;
